import React from 'react';

const LAYOUT_SHELL_READY_ATTR = 'data-layout-shell-ready';
const ROUTE_CONTENT_READY_ATTR = 'data-route-content-ready';
const PRO_LAYOUT_READY_SELECTORS = [
  '.ant-pro-layout .ant-layout-sider',
  '.ant-pro-sider.ant-layout-sider',
  '.ant-pro-layout .ant-pro-layout-content',
];
const useClientLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

function getCurrentPathname(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return window.location.pathname || '/';
}

/**
 * 当真实业务内容已经至少成功渲染过一次后，
 * 全局 `src/loading.tsx` 再次出现时就不应立即盖回整页骨架。
 *
 * 这里给“当前路径已完成首屏渲染”建立一个当前 document 生命周期内的轻量标记：
 * - 冷启动阶段：当前路径还没有 ready 标记，全局骨架允许显示，避免白屏；
 * - 同一路径已经出过真实内容后：再触发高层 fallback 时改成延迟显示，吞掉短暂闪态；
 * - 切到新路径时：旧路径的 ready 不能污染新路径，否则会把新页面误判成“重入加载”。
 */
export const ROUTE_LOADING_REENTRY_DELAY_MS = 180;

/**
 * 读取当前文档是否已经挂起过 ProLayout 外壳。
 *
 * 这个标记和“页面真实内容是否已经就绪”是两层不同语义：
 * - layout shell ready：左侧导航 / 外层容器已经可用；
 * - route content ready：当前路径的真实内容至少出现过一次。
 *
 * `src/loading.tsx` 需要靠这个标记判断：
 * - 若外壳尚未出现，允许渲染“side skeleton + 右侧业务骨架”；
 * - 若外壳已经出现，只能渲染右侧内容骨架，避免在内容区再嵌一层假 sider。
 */
export function hasLayoutShellReady(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  /**
   * 这里不能只依赖 React effect 打的 body 标记。
   *
   * 因为在某些极短窗口里：
   * - ProLayout 左侧导航已经真实挂进 DOM；
   * - 但 marker 对应的 effect 还没来得及执行；
   * - `src/loading.tsx` 会误以为“layout 还没挂起”，于是短暂回退到整页骨架。
   *
   * 所以这里增加同步 DOM 探测：
   * 只要真实的 ProLayout sider / content 节点已经存在，
   * 就立刻判定 layout shell ready，阻止右侧再闪出一层假 sider。
   */
  if (document.body?.getAttribute(LAYOUT_SHELL_READY_ATTR) === 'true') {
    return true;
  }

  return PRO_LAYOUT_READY_SELECTORS.some((selector) => Boolean(document.querySelector(selector)));
}

/**
 * 在 ProLayout 外壳首次挂载后记录标记。
 */
export function markLayoutShellReady(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.body?.setAttribute(LAYOUT_SHELL_READY_ATTR, 'true');
}

/**
 * 读取当前路径是否已经出现过真实业务内容。
 */
export function hasRouteContentReady(pathname: string = getCurrentPathname()): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.body?.getAttribute(ROUTE_CONTENT_READY_ATTR) === pathname;
}

/**
 * 在真实业务页面首次稳定挂载后记录就绪标记。
 *
 * 这个标记不会跨刷新持久化，只服务当前 document 生命周期，
 * 用来区分“当前路径首屏”与“同一路径已展示后又发生的短暂 suspense”。
 */
export function markRouteContentReady(pathname: string = getCurrentPathname()): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.body?.setAttribute(ROUTE_CONTENT_READY_ATTR, pathname);
}

/**
 * ProLayout 外壳挂起后立即打点。
 *
 * 这个 marker 只回答“layout 在不在”，不回答“页面数据是不是已经准备好”。
 */
export const LayoutShellReadyMarker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /**
   * 这里使用 layout effect，让“layout 已挂起”的标记尽量发生在浏览器绘制前。
   *
   * 即便如此，`src/loading.tsx` 仍保留 DOM 探测兜底，
   * 共同消除“左侧真实 sider 已出现，但右侧误闪整页骨架”的一帧竞争态。
   */
  useClientLayoutEffect(() => {
    markLayoutShellReady();
  }, []);

  return <>{children}</>;
};

/**
 * `childrenRender` 会包裹真实路由内容，因此这里是最稳定的打点位置：
 * - 不要求各业务页自己再额外维护一套“页面已渲染”状态；
 * - 也避免把全局 loading 的抑制逻辑散落到多个页面。
 */
export const RouteContentReadyMarker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = getCurrentPathname();

  React.useEffect(() => {
    markRouteContentReady(pathname);
  }, [pathname]);

  return <>{children}</>;
};
